/* eslint-disable react-hooks/exhaustive-deps */
import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Plus from 'apollo-react-icons/Plus';
import _ from 'lodash';
import React, { useCallback, useContext, useState } from 'react';
import { batch, useDispatch } from 'react-redux';

import EmptySection from './EmptySection';
import RepeatingTableSection from './RepeatingTableSection';
import {
  useAddingInitialRows,
  useFormSelector,
  useInitializingGridFromDatasource,
} from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../../custom-hooks/useWindowDimensions';
import { getUITA, isTrue } from '../../../utils/commonUtils';
import {
  addRow,
  bulkUpdateFormData,
  deleteFormDataRow,
  setUserMadeInteraction,
} from '../../actions/runtime.actions';
import Accordion from '../../components/Accordion';
import { FORM_MODEL_ATTRIBUTES, SS_CONTROL_TYPE } from '../../constants/form-manager.constants';
import { SSModalContext } from '../../context';
import {
  getEntityTranslation,
  getEvaluatedConditions,
  getEvaluatedExpression,
  getIsParentlessTable,
} from '../../selectors/form-manager.selectors';
import { getTranslatedEmptyMessage } from '../../selectors/runtime.selectors';
import { getEvaluatedTableRowsProperties, getFilteredRows } from '../../selectors/ssGrid.selectors';
import { getRepeatingPanelUntranslatedTitle } from '../../utils/controls/ssRepeatingPanel.utils';
import { equalityFn } from '../../utils/form-manager.utils';
import { getTableRowsEqualityFn } from '../../utils/runtime.utils';

const useStyles = makeStyles({
  addButtonContainer: {
    textAlign: 'right',
    paddingBottom: '0px !important',
  },
});

const SSRuntimeRepeatingPanel = ({
  id,
  index,
  entityName,
  childrenNodes,
  emptyMessage,
  formKey,
  isDetail,
  isViewOnly,
  initialRows,
}) => {
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const classes = useStyles();
  const { isInModal, mappingValues, targetControlId } = useContext(SSModalContext);
  const isParentlessTable = useFormSelector(getIsParentlessTable, { id });
  const selectorProps = { id, index, entityName, isParentlessTable };
  const evaluatedTitle = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.ROW_TITLE,
  });
  const header = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.HEADER,
    defaultValue: true,
  });
  const disableDeleteFilter = useFormSelector(
    getEvaluatedTableRowsProperties,
    {
      ...selectorProps,
      prop: FORM_MODEL_ATTRIBUTES.DISABLE_DELETE,
    },
    equalityFn
  );
  const disableEditFilter = useFormSelector(
    getEvaluatedTableRowsProperties,
    {
      ...selectorProps,
      prop: FORM_MODEL_ATTRIBUTES.DISABLE_EDIT,
    },
    equalityFn
  );
  const disableAdd = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.DISABLE_ADD,
  });
  const rows = useFormSelector(
    getFilteredRows,
    { ...selectorProps, mappingValues, targetControlId },
    getTableRowsEqualityFn(isParentlessTable)
  );
  const { isMobileDevice } = useWindowDimensions();

  const [isCollapsed, setCollapsed] = useState(isMobileDevice);

  const translatedEmptyMessage =
    useFormSelector(getTranslatedEmptyMessage, { emptyMessage }) ?? 'Empty section';
  const fallbackTitle = useFormSelector(getEntityTranslation, { id });

  const title = getRepeatingPanelUntranslatedTitle(evaluatedTitle, fallbackTitle);
  const translatedTitle = translateString(title);
  const showHeader = isTrue(header) && !_.isNil(title);
  const rowsLength = rows.length;
  const rowIndexKey = isParentlessTable ? 'gridArrIndex' : 'index';
  const uita = getUITA('FormPart', 'SSRepeatingPanel', entityName);
  const commonProps = {
    id,
    entityName,
    panelIndex: index,
    childrenNodes,
    showHeader,
    isDetail,
    isParentlessTable,
    rowsLength,
  };

  const handleAdd = useCallback(() => {
    batch(() => {
      dispatch(setUserMadeInteraction(formKey));
      // ContainerInstanceIdForHandMadeNewObject is used to mark that the row was added by clicking on 'Add' button
      dispatch(
        addRow({
          entityName,
          index,
          initialRowData: { ContainerInstanceIdForHandMadeNewObject: id },
          formKey,
          isParentlessTable,
        })
      );

      if (isInModal) {
        const changesArray = Object.entries(mappingValues).map(([updateTo, value]) => {
          const [entityName, fieldName] = updateTo.split('.');
          return { entityName, fieldName, value, index: [0] };
        });

        dispatch(bulkUpdateFormData(changesArray, formKey));
      }
    });
  }, [JSON.stringify(index), mappingValues]);

  const handleDelete = useCallback(
    (row) => {
      dispatch(setUserMadeInteraction(formKey));
      dispatch(
        deleteFormDataRow({
          rowIndex: isParentlessTable ? _.last(row.gridArrIndex) : row.index,
          index: isParentlessTable ? row.gridArrIndex : index,
          entityName,
          formKey,
        })
      );
    },
    [JSON.stringify(index)]
  );

  useAddingInitialRows({
    initialRows,
    selectorProps,
  });

  useInitializingGridFromDatasource({
    rowsLength,
    selectorProps,
  });

  if (isDetail) {
    return (
      <div ui-ta={uita}>
        {rows.map((row, rowIndex) => (
          <RepeatingTableSection
            {...commonProps}
            key={row.key}
            row={row}
            rowIndex={rowIndex}
            disableDelete
            viewOnly
            hasPaddings
          />
        ))}
      </div>
    );
  }

  if (disableAdd && rowsLength === 1) {
    const row = rows[0];

    return (
      <div ui-ta={uita}>
        <RepeatingTableSection
          {...commonProps}
          key={row.key}
          row={row}
          disableDelete={disableDeleteFilter[row[rowIndexKey]] || isViewOnly}
          viewOnly={isViewOnly || disableEditFilter[row[rowIndexKey]]}
          handleDelete={handleDelete}
          hasPaddings
        />
      </div>
    );
  }

  return (
    <div ui-ta={uita}>
      <Accordion
        isCollapsed={isCollapsed}
        showHeader={showHeader}
        title={translatedTitle}
        controlType={SS_CONTROL_TYPE.REPEATING_PANEL}
        onChange={() => setCollapsed(!isCollapsed)}
        autoScrollUpEnabled={isMobileDevice && isCollapsed}
      >
        <>
          {showHeader && !disableAdd && !isViewOnly && (
            <div className={classes.addButtonContainer}>
              <span ui-ta={getUITA('FormCtrl', 'AddButton')}>
                <Button color="primary" variant="secondary" onClick={handleAdd}>
                  <Plus fontSize="small" />
                  &nbsp;
                  {translateString('Add')}
                </Button>
              </span>
            </div>
          )}
          <div>
            {rowsLength ? (
              rows.map((row, rowIndex) => (
                <RepeatingTableSection
                  {...commonProps}
                  key={row.key}
                  row={row}
                  rowIndex={rowIndex}
                  disableDelete={disableDeleteFilter[row[rowIndexKey]] || isViewOnly}
                  viewOnly={isViewOnly || disableEditFilter[row[rowIndexKey]]}
                  handleDelete={handleDelete}
                />
              ))
            ) : (
              <EmptySection emptyMessage={translatedEmptyMessage} />
            )}
          </div>
        </>
      </Accordion>
    </div>
  );
};

export default SSRuntimeRepeatingPanel;
